import {
  BadgeCheck,
  CalendarCog,
  CalendarDays,
  Camera,
  Car,
  CarFront,
  CircleQuestionMark,
  ClipboardList,
  Flag,
  House,
  Inbox,
  MapIcon,
  MapPin,
  Newspaper,
  Palette,
  Settings,
  ShieldUser,
  Trophy,
  Upload,
  User,
  UserCog,
  Users,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import type { SharedProps } from "@/types";

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Active on this exact path only, for an item whose href prefixes its siblings' (/admin). */
  exact?: boolean;
  /** Also active anywhere under this prefix, for a section whose pages don't sit under href. */
  activePrefix?: string;
  /** A count shown beside the label, read from the shared props. */
  count?: (props: SharedProps) => number;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

export const SETTINGS_ITEM: NavItem = {
  label: "Settings",
  href: "/settings",
  icon: Settings,
};

const pendingCarReviews = (props: SharedProps) => props.pendingCarReviews;

// Everything a signed-in user can do except Tracks, Events and Clubs, which
// stay in the top bar. The two grants are independent — somebody can hold
// both — so the Manage entries stack rather than branch against each other.
//
// This is the only way between manager sections: the manager pages have no tab
// bar of their own any more, just a title and their actions (ManagerPageHeader).
// An item left out here is unreachable without typing its URL.
export function navSections(props: SharedProps): NavSection[] {
  const user = props.currentUser;
  if (!user) return [];

  const manage: NavItem[] = [];
  if (user.trackManager) {
    manage.push(
      { label: "Events", href: "/track_manager/events", icon: CalendarCog },
      {
        label: "Credentials",
        href: "/track_manager/credentials",
        icon: BadgeCheck,
      },
      {
        label: "Applications",
        href: "/track_manager/applications",
        icon: Inbox,
      },
      { label: "Seasons", href: "/track_manager/seasons", icon: Trophy },
      { label: "Tracks", href: "/track_manager/tracks", icon: MapPin },
      {
        label: "Car reviews",
        href: "/track_manager/car_reviews",
        icon: CarFront,
        count: pendingCarReviews,
      },
    );
  }
  if (user.clubManager) {
    manage.push({
      label: "Club events",
      href: "/club_manager/events",
      icon: Flag,
    });
  }
  // Only for a club an admin has switched blogs on for. The page behind it
  // still explains itself if somebody reaches it by URL with them off.
  if (props.clubBlogsEnabled) {
    manage.push({
      label: "Club blog",
      href: "/club_manager/blogs",
      icon: Newspaper,
    });
  }
  // Only once an admin has set a brand up for one of their tracks or clubs —
  // it's a paid extra. The page behind it explains itself if reached by URL.
  if (props.brandsManaged) {
    manage.push({
      label: "Branding",
      href: "/branding",
      icon: Palette,
      activePrefix: "/branding",
    });
  }
  if (!user.trackManager && !user.clubManager) {
    manage.push({
      label: "Host events?",
      href: "/track_manager/verification",
      icon: CircleQuestionMark,
    });
  }

  const sections: NavSection[] = [
    {
      items: [
        { label: "Home", href: "/dashboard", icon: House, activePrefix: "/dashboard" },
        { label: "Calendar", href: "/calendar", icon: CalendarDays },
        { label: "Applications", href: "/applications", icon: ClipboardList },
      ],
    },
    {
      title: "You",
      items: [
        { label: "Profile", href: "/profile", icon: User },
        { label: "Garage", href: "/garage", icon: Car },
        // Edit redirects to new when there isn't one yet.
        {
          label: "Media group",
          href: "/media_group_participant/edit",
          icon: Camera,
          activePrefix: "/media_group_participant",
        },
        {
          label: "My team",
          href: "/my-team",
          icon: Users,
          activePrefix: "/team_participant",
        },
      ],
    },
    { title: "Manage", items: manage },
  ];

  if (user.admin) {
    sections.push({
      title: "Admin",
      items: [
        { label: "Admin home", href: "/admin", icon: ShieldUser, exact: true },
        { label: "Tracks", href: "/admin/tracks", icon: MapIcon },
        {
          label: "Track managerships",
          href: "/admin/track_managers",
          icon: UserCog,
        },
        {
          label: "Club managerships",
          href: "/admin/club_managers",
          icon: UsersRound,
        },
        { label: "Onboard track", href: "/admin/onboarding", icon: Upload },
        // A track manager already has this under Manage.
        ...(user.trackManager
          ? []
          : [
              {
                label: "Car reviews",
                href: "/track_manager/car_reviews",
                icon: CarFront,
                count: pendingCarReviews,
              },
            ]),
      ],
    });
  }

  return sections;
}

export function isActive(item: NavItem, path: string): boolean {
  if (item.exact) return path === item.href;
  if (item.activePrefix && path.startsWith(item.activePrefix)) return true;
  return path === item.href || path.startsWith(`${item.href}/`);
}
